import Modal from './Modal';

const PrivacyModal = ({ isOpen, onClose }) => {
  return (
    <Modal isOpen={isOpen} onClose={onClose} size="95%" title="Privacy Policy">
      <div className="p-6 max-h-[80vh] overflow-y-auto">
        <div className="prose prose-neutral max-w-none">
          <p className="text-sm text-neutral-600 mb-6">
            <strong>Last updated:</strong> {new Date().toLocaleDateString('en-AU', {
              year: 'numeric',
              month: 'long',
              day: 'numeric'
            })}
          </p>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-neutral-800 mb-4">1. Introduction</h2>
            <p className="text-neutral-700 mb-4">
              Melbourne Floral Hire ("we," "our," or "us") is committed to protecting your privacy and ensuring the security of your personal information. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website, use our services, or interact with us.
            </p>
            <p className="text-neutral-700 mb-4">
              This policy complies with the Australian Privacy Principles (APPs) under the Privacy Act 1988 (Cth), the EU General Data Protection Regulation (GDPR), the California Consumer Privacy Act (CCPA), and other applicable privacy laws.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-neutral-800 mb-4">2. Information We Collect</h2>

            <h3 className="text-xl font-semibold text-neutral-800 mb-3">2.1 Personal Information</h3>
            <p className="text-neutral-700 mb-4">We may collect the following types of personal information:</p>
            <ul className="list-disc pl-6 text-neutral-700 mb-4">
              <li>Name, email address, phone number, and postal address</li>
              <li>Event details (date, venue, type of event)</li>
              <li>Payment information (processed securely through third-party providers)</li>
              <li>Communication preferences and history</li>
              <li>IP address, browser type, and device information</li>
            </ul>

            <h3 className="text-xl font-semibold text-neutral-800 mb-3">2.2 How We Collect Information</h3>
            <ul className="list-disc pl-6 text-neutral-700 mb-4">
              <li>Directly from you when you contact us, request quotes, or make inquiries</li>
              <li>Through our website forms and booking systems</li>
              <li>Automatically through cookies and similar technologies</li>
              <li>From third-party service providers (e.g., payment processors)</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-neutral-800 mb-4">3. How We Use Your Information</h2>
            <p className="text-neutral-700 mb-4">We use your personal information for the following purposes:</p>
            <ul className="list-disc pl-6 text-neutral-700 mb-4">
              <li>To provide and deliver our floral hire services</li>
              <li>To process payments and manage transactions</li>
              <li>To communicate with you about your orders, events, and inquiries</li>
              <li>To send you marketing communications (with your consent)</li>
              <li>To improve our services and website functionality</li>
              <li>To comply with legal obligations</li>
              <li>To prevent fraud and ensure security</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-neutral-800 mb-4">4. Information Sharing and Disclosure</h2>
            <p className="text-neutral-700 mb-4">We do not sell, trade, or rent your personal information to third parties. We may share your information in the following circumstances:</p>
            <ul className="list-disc pl-6 text-neutral-700 mb-4">
              <li><strong>Service Providers:</strong> With trusted third-party service providers who assist us in operating our business (e.g., payment processors, delivery services)</li>
              <li><strong>Legal Requirements:</strong> When required by law, court order, or government regulation</li>
              <li><strong>Business Protection:</strong> To protect our rights, property, or safety, or that of our customers</li>
              <li><strong>Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-neutral-800 mb-4">5. Cookies and Tracking Technologies</h2>
            <p className="text-neutral-700 mb-4">
              We use cookies and similar technologies to enhance your browsing experience, analyze website traffic, and personalize content. You can control cookie settings through your browser preferences.
            </p>
            <p className="text-neutral-700 mb-4">
              We do not use cookies for tracking or advertising purposes without your explicit consent.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-neutral-800 mb-4">6. Data Security</h2>
            <p className="text-neutral-700 mb-4">
              We implement appropriate technical and organizational measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. These measures include:
            </p>
            <ul className="list-disc pl-6 text-neutral-700 mb-4">
              <li>SSL/TLS encryption for data transmission</li>
              <li>Secure data storage and access controls</li>
              <li>Regular security assessments and updates</li>
              <li>Employee training on data protection</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-neutral-800 mb-4">7. Data Retention</h2>
            <p className="text-neutral-700 mb-4">
              We retain your personal information only as long as necessary to fulfill the purposes outlined in this Privacy Policy, unless a longer retention period is required by law. When information is no longer needed, we securely delete or anonymize it.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-neutral-800 mb-4">8. Your Rights</h2>
            <p className="text-neutral-700 mb-4">Depending on your location, you may have the following rights regarding your personal information:</p>

            <h3 className="text-xl font-semibold text-neutral-800 mb-3">8.1 General Rights</h3>
            <ul className="list-disc pl-6 text-neutral-700 mb-4">
              <li><strong>Access:</strong> Request a copy of the personal information we hold about you</li>
              <li><strong>Correction:</strong> Request correction of inaccurate or incomplete information</li>
              <li><strong>Deletion:</strong> Request deletion of your personal information (subject to legal requirements)</li>
              <li><strong>Portability:</strong> Request transfer of your data in a structured format</li>
              <li><strong>Objection:</strong> Object to processing based on legitimate interests</li>
              <li><strong>Restriction:</strong> Request limitation of processing in certain circumstances</li>
            </ul>

            <h3 className="text-xl font-semibold text-neutral-800 mb-3">8.2 California Residents (CCPA)</h3>
            <p className="text-neutral-700 mb-4">
              California residents have additional rights under the CCPA, including the right to know what personal information we collect and the right to opt-out of the sale of personal information (though we do not sell personal information).
            </p>

            <h3 className="text-xl font-semibold text-neutral-800 mb-3">8.3 EU Residents (GDPR)</h3>
            <p className="text-neutral-700 mb-4">
              EU residents have rights under the GDPR, including the right to lodge a complaint with a supervisory authority.
            </p>

            <p className="text-neutral-700 mb-4">
              To exercise any of these rights, please contact us using the information provided below.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-neutral-800 mb-4">9. International Data Transfers</h2>
            <p className="text-neutral-700 mb-4">
              Your information may be transferred to and processed in countries other than Australia. We ensure that such transfers comply with applicable data protection laws and implement appropriate safeguards.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-neutral-800 mb-4">10. Children's Privacy</h2>
            <p className="text-neutral-700 mb-4">
              Our services are not intended for children under 13. We do not knowingly collect personal information from children under 13. If we become aware that we have collected such information, we will promptly delete it.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-neutral-800 mb-4">11. Third-Party Links</h2>
            <p className="text-neutral-700 mb-4">
              Our website may contain links to third-party websites. We are not responsible for the privacy practices or content of these external sites. We encourage you to review the privacy policies of any third-party sites you visit.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-neutral-800 mb-4">12. Changes to This Privacy Policy</h2>
            <p className="text-neutral-700 mb-4">
              We may update this Privacy Policy from time to time. We will notify you of any material changes by posting the new policy on our website and updating the "Last updated" date. Your continued use of our services after such changes constitutes acceptance of the updated policy.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-neutral-800 mb-4">13. Contact Us</h2>
            <p className="text-neutral-700 mb-4">
              If you have any questions about this Privacy Policy or our data practices, please contact us:
            </p>
            <div className="bg-neutral-50 p-4 rounded-lg">
              <p className="text-neutral-700"><strong>Melbourne Floral Hire</strong></p>
              <p className="text-neutral-700">Warehouse 10, 30 Tower Court</p>
              <p className="text-neutral-700">Noble Park VIC 3174</p>
              <p className="text-neutral-700">Email: hire@melbournefloralhire.com.au</p>
              <p className="text-neutral-700">Phone: 0466 645 060</p>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-neutral-800 mb-4">14. Complaints</h2>
            <p className="text-neutral-700 mb-4">
              If you believe we have breached your privacy rights, please contact us first. If you are not satisfied with our response, you may lodge a complaint with the relevant privacy regulator:
            </p>
            <ul className="list-disc pl-6 text-neutral-700 mb-4">
              <li><strong>Australia:</strong> Office of the Australian Information Commissioner</li>
              <li><strong>EU:</strong> Your local data protection authority</li>
              <li><strong>California:</strong> California Attorney General</li>
            </ul>
          </section>
        </div>
      </div>
    </Modal>
  );
};

export default PrivacyModal;